import React, { useEffect, useState } from "react";

import {
    Box,
    Skeleton,
    TableBody,
    TableCell,
    TableRow,
    Theme,
    Typography,
    useTheme
} from "@mui/material";
import {
    TableBodyComponentProps,
    TableBodyLoaderProps
} from "../../types";


const TableBodyLoader = ({
    rowsNum,
    colsNum,
    rowCustomStyles,
    cellStyles = [],
}: TableBodyLoaderProps) => {
    const theme = useTheme()

    return (
        <>
            {[...Array(rowsNum)].map((_, index) => (
                <TableRow
                    key={index}
                    sx={{
                        '& td': {
                            paddingTop: "15px",
                            paddingBottom: "15px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                        },
                        ...rowCustomStyles
                    }}
                >
                    {[...Array(colsNum)].map((_, colIndex) => (
                        <TableCell key={colIndex} sx={cellStyles[colIndex] ?? {}}>
                            <Skeleton animation="wave" variant="text" aria-hidden="true" />
                        </TableCell>
                    ))}
                </TableRow>
            ))}
        </>
    );
};




export default function TableBodyComponent(
    {
        isLoading,
        tableDataCount,
        tableBodyCustomStyles,
        loadingRowsNum,
        loadingColsNum,
        tableBodyLoaderRowCustomStyles,
        tableLoaderCellStyles = [],
        tableBody,
        pinnedRow,
        noResultMsg = "No Data Found",
    }: TableBodyComponentProps
): JSX.Element {
    const theme = useTheme();
    const [announcement, setAnnouncement] = useState("");

    useEffect(() => {
        let timeout: NodeJS.Timeout
        if (isLoading) {
            setAnnouncement("");
            timeout = setTimeout(() => {
                setAnnouncement("Loading table data");
            }, 50);
        } else if (!isLoading && tableDataCount > 0) {
            setAnnouncement("");
            timeout = setTimeout(() => {
                setAnnouncement("Table data loaded");
            }, 50);
        }
        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [isLoading, tableDataCount]);

    const hasPinnedRow = pinnedRow !== undefined && pinnedRow !== null

    return (
        <>
            {/* Screen reader live region */}
            <Box
                role="status"
                aria-live="assertive"
                aria-atomic="true"
                sx={{
                    position: "absolute",
                    width: "1px",
                    height: "1px",
                    padding: 0,
                    margin: "-1px",
                    overflow: "hidden",
                    clip: "rect(0, 0, 0, 0)",
                    whiteSpace: "nowrap",
                    border: 0,
                }}
            >
                {announcement}
            </Box>
            <TableBody
                sx={{
                    ...tableBodyCustomStyles,
                    background: theme.palette.customColors.darkGrey,
                }}
            >
                {hasPinnedRow && !isLoading && pinnedRow}
                {isLoading && (
                    <TableBodyLoader
                        rowsNum={loadingRowsNum}
                        colsNum={loadingColsNum}
                        rowCustomStyles={tableBodyLoaderRowCustomStyles}
                        cellStyles={tableLoaderCellStyles}
                    />
                )}
                {!isLoading && tableDataCount > 0 && (
                    tableBody
                )}
                {(!isLoading && tableDataCount === 0 && hasPinnedRow) && (
                    <TableRow>
                        <TableCell
                            colSpan={loadingColsNum}
                            align="center"
                            sx={{
                                paddingTop: "20px",
                                paddingBottom: "20px",
                                borderBottom: 0,
                            }}
                        >
                            <Typography
                                variant="body_600"
                                sx={{ color: theme.palette.text.primary }}
                            >
                                {noResultMsg}
                            </Typography>
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </>
    )
}